import React from 'react'
import { connect } from 'react-redux'
import { keyBy, flatten, keys } from 'lodash'
import { Glyphicon, Table, Modal, Row, Col, Checkbox } from 'react-bootstrap'
import { createSelector } from 'reselect'
import Markdown from 'react-markdown'

import {
  getAllActivities,
  getAllRoutes,
  getSuppliers,
  getTripFromProps,
  getTripReservations,
  makeGetCityLabel,
  getBillForProps
} from '../selectors'

import { getText } from '../lib/text'
import { getUnusedReservations, getBrokenReservations } from '../../lib/TripTools'
import { linkifyMarkdown } from '../../lib/Tools'
import Button from '../components/Button'
import Reservation from './Reservation.jsx'
import { Sumup, PaymentsSumup } from './DesgloseSumup'
import FacturaForm from './FacturaForm.jsx'
import { UnusedReservations, BrokenReservations } from './components/DesgloseErrors'
import CommissionsList from './components/CommissionsList'
import { getItemKey, createReservationFromItem } from './createReservationFromItem'
import IfAllowed from "../components/IfAllowed"
import MarkdownField from "../lib/MarkdownField.jsx"

import {
  createReservation, updateReservation, removeReservation
} from '../ducks/reservations.js'

import "./Desglose.less"
import store from '../../tests/mockStore/mockStore';
import { remove, editItem } from '../ducks/routes';

/**
 * CSV Generation
 */

// #NOTE: this should be removed at some point in the near future

function surround(x) { return `"${x}"` }

function generateCSVData(trip, bill) {
  const lines = []
  const reservationNotes = trip.reservationNotes || {}
  const reservationDone = trip.reservationDone || {}

  lines.push("Item, Cálculo precio, Precio (€), Cálculo Coste, Coste (€), Comprado, Notas")

  bill.forEach(section => {
    lines.push(section.sectionName)

    section.items.forEach(item => {
      lines.push([
        item.name || "",
        item.priceCalculation || "",
        item.priceValue,
        item.costCalculation || "",
        item.costValue,
        reservationDone[item._id] ? "Sí" : "No",
        reservationNotes[item._id] || ""
      ].map(surround).join(','))
    })
  })

  return lines.join("\n")
}

/**
 * selector: get reservations that have some kind of error
 */

export const getErrorReservations = createSelector(
  [
    getTripFromProps,
    getTripReservations,
    getAllRoutes,
    (state, props) => getBillForProps(state, props),
  ],
  (trip, tripReservations, Routes, bill) => {
    const route = Routes[trip.mainRoute]
    const unusedReservations = getUnusedReservations(trip, tripReservations, route, bill)
    const brokenReservations = getBrokenReservations(trip, tripReservations, route, bill)

    return { unusedReservations, brokenReservations }
  }
)

/**
 * Lista de desglose
 */

type Props = {
  trip: Object,
  tripReservations: Object,
  bill: Object[],
  editTrip: Object => void,
  suppliers: Object,
  activities: Object,
  errorReservations: { unusedReservations: String[], brokenReservations: { [String]: Object[] } },

  // mapped from dispatch
  createReservation: (Object, Object, string) => void,
  updateReservation: (string, Object) => void,
  removeReservation: string => void,
}

const reservationItemTypes = new Set([ "note", "hotel", "expense", "activity", "city", "other_expense" ])

class Desglose extends React.PureComponent
{
  constructor(props) {
    super(props)
    this.state = { csvModal: false, isDetailed: false, isToggled: false }

    this.boundOnChange = this._onReservationChange.bind(this)
  }

  // #TODO move this logic away from the presentation code
  createReservation(item, initials) {
    this.props.createReservation(initials, item, this.props.trip._id)
  }

  removeReservation(id) {
    this.props.removeReservation(id)
  }

  approveChanges(id: string, brokenFields: [ { field: string, newValue: any, rValue: any } ]) {
    const x = {}
    brokenFields.forEach(c => { x[c.field] = c.newValue })
    this.props.updateReservation(id, x)
  }

  props: Props

  getReservationForItem(item) {
    const { trip, tripReservations } = this.props

    // TODO dont duplicate reservation-creation code

    // NOTE the following code to populate an empty reservation with info from the
    // item is duped from the initials in the creation action

    return (
      tripReservations[trip._id + getItemKey(item)] || createReservationFromItem(trip._id, item, null)
    )
  }

  _onReservationChange(reservationId, item, changes) {
    // reservations with no id are just empty proxies
    if (reservationId) {
      this.props.updateReservation(reservationId, changes)
    }
    else {
      this.createReservation(item, changes)
    }
  }

  renderNote(item, itemIdxIgnored) {
    return (
      <tr className="note">
        <td colSpan="999">
          <MarkdownField
            label="Nota interna"
            value={item.internalText || ""}
            onChange={x => this.props.editNote(item.key, x)  }
            onRemoveClick={() => { this.props.removeNote(item.key) }}
          />              
        </td>
      </tr>
    )
  }

  renderRow(item, itemIdx, error) {
    // reservations use referenced key instead of key in repeated expenses
    const { trip, route, Activities, Suppliers } = this.props
    const reservation = this.getReservationForItem(item)

    return (
      <Reservation
        includeTrip={false}
        key={itemIdx}
        billItem={item}
        {...({ reservation, trip, Activities, Suppliers, route, error })}
        onChange={this.boundOnChange/* rid, item, changes */}
      />
    )
  }

  render() {
    const { trip, tripReservations, bill, suppliers } = this.props

    const billItems = keyBy(
      flatten(bill.map(s => s.items)),
      "key"
    )

    const reservations = tripReservations
    const { unusedReservations, brokenReservations } = this.props.errorReservations
    const errorReservationsSet = new Set([...unusedReservations, ...keys(brokenReservations)])

    const CSVModal = () => (
      <Modal show onHide={() => this.setState({ csvModal: false })}>
        <Modal.Header closeButton onHide={() => this.setState({ csvModal: false })}>
          <Modal.Title>{`Desglose ${trip.name}.csv`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <a
            download={`Desglose ${trip.name}.csv`}
            href={`data:application/octet-stream,${encodeURIComponent(generateCSVData(trip, bill))}`}
          >
            {getText("Descargar")}
          </a>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => this.setState({ csvModal: false })}>{getText("Cerrar")}</Button>
        </Modal.Footer>
      </Modal>
    )

    return (
      <IfAllowed verb="read" collection="reservations" else={<h1>No disponible</h1>}>
        <h3>
          {getText("Desglose de gastos")} (<Glyphicon glyph="star" />)

          <Button
            className="desgloseDownload"
            style={{ marginLeft: 5}}
            glyph="download"
            onClick={() => this.setState({csvModal: true})}
          >
            {getText("Descargar CSV")}
          </Button>
        </h3>

        { this.state.csvModal && (<CSVModal />) }

        <Row>
          <Col md={7} sm={12}>
            <FacturaForm trip={trip} editTrip={changes => this.props.editTrip(changes)} />

            <hr style={{maxWidth: "80%", margin: "auto", marginTop: 10, marginBottom: 10}} />

            <label>{"Comisiones y descuentos:"}</label>
            <CommissionsList
              trip={trip}
              onBookingChange={commission => this.props.editTrip({ commission })}
              onListChange={commissions => this.props.editTrip({ commissions })}
            />

            <UnusedReservations
              trip={trip}
              reservations={reservations}
              unusedReservations={unusedReservations}
              removeReservation={id => this.removeReservation(id)}
              suppliers={suppliers}
            />

            <BrokenReservations
              trip={trip}
              reservations={reservations}
              billItems={billItems}
              brokenReservations={brokenReservations}
              approve={(id, brokenFields) => this.approveChanges(id, brokenFields)}
              remove={id => this.removeReservation(id)}
              suppliers={suppliers}
            />
          </Col>

          <Col md={5} sm={12}>
            <PaymentsSumup trip={trip} />
            {
              this.state.isDetailed ? (
                <Button
                  className = "btnDetalles"
                  glyph="minus"
                  onClick={() => this.setState({isDetailed: false})}
                >
                  {getText("Menos detalles")}
                </Button>
              ) : (
                <Button
                  className = "btnDetalles"
                  glyph="plus"
                  onClick={() => this.setState({isDetailed: true})}
                >
                  {getText("Más detalles")}
                </Button>
              )
            }

            <Sumup
              trip={trip}
              bill={bill}
              reservations={tripReservations}
              editTrip={this.props.editTrip}
              isDetailed={this.state.isDetailed}
            />
          </Col>
        </Row>

        <Checkbox
          style={{ marginBottom: 10 }}
          checked={this.state.isToggled}
          onChange={evt => this.setState({isToggled: evt.target.checked}) }
        >
          <span style={{fontWeight: "bold"}}>{getText("Mostrar/ocultar SR y NN")}</span>
        </Checkbox>

        {/* Esta cabecera de la tabla es sticky; se ve siempre en la página, nunca en el print */}
        <div className = "stickyHeader">
          <Table style={{margin: 0}}>
            <tbody>
              <tr>
                {
                  [
                    [145, "Item"],
                    [220, "Estado reserva"],
                    [160, "Precio plataforma"],
                    [120, "Base imponible"],
                    [120, "Total"],
                    [300, "Notas"]
                  ]
                    .map(([width, text]) => (
                      <td key={text} style={{ width }}><strong>{getText(text)}</strong></td>
                    ))
                }
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Esta cabecera de la tabla es fija y solo aparecerá en el print */}

        <Table>
          <thead className="realHeader" style={{display:"none"}}>
            <tr>
              {
                ["Item", "Estado reserva", "Precio plataforma", "Base imponible", "Total", "Notas"]
                  .map(x => <th key={x}>{getText(x)}</th>)
              }
            </tr>
          </thead>
          <tbody className="desglose-list">
            {
              bill.map(section => (
                [
                  <tr className="trDia" key={`trdia${section.key}`}>
                    <td colSpan={6}><strong>{section.sectionName}</strong></td>
                  </tr>
                ]
                  .concat(
                    section.items
                      .filter(item => {
                        if (!this.state.isToggled) return true
                        else {
                          const {status} = this.getReservationForItem(item)
                          return status && status != "sr" && status != "nn"
                        }
                      })
                      .filter(item => reservationItemTypes.has(item.type))
                      .map((item, itemIdx) => (
                        item.type == "note"
                          ? this.renderNote(item, itemIdx)
                          : this.renderRow(
                            item,
                            itemIdx,
                            (
                              errorReservationsSet.has(item.key)
                              || (item.type == "expense" && errorReservationsSet.has(item.agendaItem.ref))
                            )
                          )
                      ))
                  )
              ))
            }
          </tbody>
        </Table>
      </IfAllowed>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  getCityLabel: makeGetCityLabel(state, ownProps),
  suppliers: getSuppliers(state, ownProps),
  activities: getAllActivities(state, ownProps),

  tripReservations: getTripReservations(state, ownProps),
  errorReservations: getErrorReservations(state, ownProps),

  bill: getBillForProps(state, ownProps),
})

const mapDispatchToProps = (dispatch, ownProps) => ({
  createReservation: (initials, item, tripId) => dispatch(createReservation(initials, item, tripId)),
  updateReservation: (id, changes) => dispatch(updateReservation(id, changes)),
  removeReservation: id => dispatch(removeReservation(id)),

  removeNote: (itemKey) => {
    const { route } = ownProps
    const itarget = route.agenda.findIndex(item => item.key == itemKey)
    dispatch(remove(ownProps.route._id, itarget))
  },

  editNote: (itemKey, internalText) => {
    const { route } = ownProps
    const itarget = route.agenda.findIndex(item => item.key == itemKey)
    dispatch(editItem(ownProps.route._id, itarget, { internalText }))
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(Desglose)
